import { identityScript, viewDocument } from "./document.js";

// `review_result` / `set_review_status` ask the clinician to attest. The view shows exactly what is being attested,
// and only Confirm calls the app-only `record_review`; "Not yet" writes nothing (FR-6).
const body = `<div id="root"><p class="muted">Loading…</p></div>`;
const script = `${identityScript}
var root = document.getElementById("root"), state = null, requestId = null, done = false;
var CHART = { medReviewed: "medications", allergyReviewed: "allergies", historyReviewed: "history" };
// The read that has each chart attestation's subject as it is now.
var SUBJECT_READ = { medReviewed: "get_medications", allergyReviewed: "get_chart_summary", historyReviewed: "get_visit_history" };
function resultHtml(r) {
  return '<div class="panel"><strong>' + view.esc(r.name) + '</strong>' + (r.reviewed ? ' <span class="badge">Already reviewed</span>' : '') + '<p>' + view.esc(r.value) + ' ' + view.esc(r.unit) +
    ' <span class="muted">(range ' + view.esc(r.range) + ')</span>' + (r.flag ? ' <span class="badge warn">' + view.esc(r.flag) + '</span>' : '') + '</p>' +
    '<p class="muted">Collected ' + view.esc(r.collected) + ' · ' + view.esc(r.specimen) + (r.previous ? ' · previous ' + view.esc(r.previous) : '') + '</p>' +
    (r.detail ? '<p>' + view.esc(r.detail) + '</p>' : '') + '</div>';
}
function subjectHtml(s) {
  if (s.kind === "allergy") return '<div class="panel"><strong>Allergy</strong><p>' + view.esc(s.allergy) + ' <span class="muted">— ' + view.esc(s.reaction) + '</span></p></div>';
  if (s.kind === "history") return '<div class="panel"><table><tbody>' + s.visits.map(function (v) {
    return '<tr><td>' + view.esc(v.date) + '</td><td>' + view.esc(v.summary) + '</td><td>' + view.esc(v.status) + '</td></tr>'; }).join("") + '</tbody></table></div>';
  return '<div class="panel"><table><tbody>' + s.medications.map(function (m) {
    return '<tr><td>' + view.esc(m.name) + ' ' + view.esc(m.strength) + '</td><td>' + view.esc(m.directions) + '</td></tr>'; }).join("") + '</tbody></table></div>';
}
/** What the clinician attests, as rendered: the part a re-read compares. */
function shown(p) {
  var item = p.item;
  return item.kind === "results" ? item.results.map(resultHtml).join("") : subjectHtml(item.subject) + (item.current ? " current" : "");
}
function render() {
  var p = state, item = p.item, isResult = item.kind === "results";
  var undo = !isResult && !item.reviewed;
  var already = isResult ? item.results.every(function (r) { return r.reviewed; }) : item.current === item.reviewed;
  var html = identityHtml(p.patient, p.encounter);
  html += '<h2>' + (isResult ? (item.results.length > 1 ? 'Results' : 'Result') : 'Review of ' + view.esc(CHART[item.field])) + '</h2>';
  html += isResult ? item.results.map(resultHtml).join("") : subjectHtml(item.subject);
  html += '<p class="muted">Nothing is recorded until you confirm. Confirm only what you reviewed yourself.</p>';
  if (already && !done) html += '<p class="muted">This is already recorded as ' + (undo ? 'not reviewed' : 'reviewed') + '.</p>';
  var locked = done || p.encounter.signed;
  html += '<div class="actions"><button id="no"' + (done ? ' disabled' : '') + '>Not yet</button><button id="yes" class="primary"' + (locked ? ' disabled' : '') + '>' +
    (undo ? 'Undo review' : 'Confirm I reviewed this') + '</button></div>';
  root.innerHTML = html;
  if (p.encounter.signed) showStatus(root, "This encounter is signed, so reviews can no longer change.", true);
  document.getElementById("no").onclick = function () { done = true; render(); showStatus(root, "Not recorded. Nothing was written."); };
  document.getElementById("yes").onclick = confirm_;
  view.reportSize();
}
function confirm_() {
  var item = state.item, args = {
    patient_id: state.patient.patient_id, encounter_id: state.encounter.id, expected_revision: state.encounter.revision,
    request_id: (requestId = requestId || view.requestId())
  };
  if (item.kind === "results") args.result_ids = item.results.map(function (r) { return r.id; });
  else { args.field = item.field; args.reviewed = item.reviewed; }
  document.getElementById("yes").disabled = true;
  view.callTool("record_review", args).then(function (r) {
    if (!r.ok) {
      if (r.code !== "stale_revision") { document.getElementById("yes").disabled = false; showStatus(root, explain(r), true); return; }
      done = true; requestId = null; render();
      afterStale(root, "confirm", state, reread, shown, function (fresh) { state = fresh; done = false; render(); });
      return;
    }
    done = true; state.encounter.revision = r.data.revision; render();
    showStatus(root, "Recorded. The encounter is now revision " + r.data.revision + ".");
  });
}
function reread() {
  var p = state, item = p.item, patient_id = p.patient.patient_id;
  if (item.kind === "results") return read("get_results", { patient_id: patient_id }).then(function (d) {
    var results = item.results.map(function (r) { return d.results.filter(function (x) { return x.id === r.id; })[0]; });
    if (results.some(function (r) { return !r; })) throw new Error("A result is no longer on this chart.");
    return { revision: d.encounter.revision, signed: d.encounter.signed, state: { kind: p.kind, patient: p.patient, encounter: d.encounter, item: { kind: "results", results: results } } };
  });
  // The revision first: a subject read after it is at least as new, so a Confirm at that revision cannot attest
  // anything older than what it shows (a newer subject only makes the write stale again).
  return read("get_encounter", { patient_id: patient_id, encounter_id: p.encounter.id }).then(function (d) {
    var e = d.encounter;
    return read(SUBJECT_READ[item.field], { patient_id: patient_id }).then(function (s) {
      var subject = item.subject.kind === "medications" ? { kind: "medications", medications: s.active }
        : item.subject.kind === "allergy" ? { kind: "allergy", allergy: s.patient.allergy, reaction: s.patient.reaction }
        : { kind: "history", visits: s.visits };
      return { revision: e.revision, signed: e.signed, state: { kind: p.kind, patient: p.patient, encounter: { id: e.id, revision: e.revision, signed: e.signed },
        item: { kind: "chart", field: item.field, reviewed: item.reviewed, current: e[item.field], subject: subject } } };
    });
  });
}
view.onResult(function (data) {
  if (!data || data.kind !== "review_confirm") { root.textContent = "Nothing to confirm."; return; }
  state = data; requestId = null; done = false; render();
});
`;
export const reviewConfirmView = viewDocument("Confirm review", body, script);
